import SolidPlaneDepartureAltIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureAltIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { fillArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useIntl } from 'react-intl'

const TimeLineContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  column-gap: ${rem(8)};
  align-items: center;
`

const FlightInfoContainer = styled(Group)`
  width: 100%;
`

const FlightLineContainer = styled(Group)`
  width: 100%;
`

const FlightLine = styled.div`
  width: 100%;
  height: 1px;
  min-height: 1px;
  position: relative;
  flex: 1;
  background-color: ${props => props.theme.palette.neutral.surface.darkForeground};
`

const FlightLineDots = styled(Group)`
  position: absolute;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  height: ${rem(8)};

  [dir='rtl'] & {
    transform: translate(50%, -50%);
  }
`

const FlightLineDot = styled.div`
  width: ${rem(8)};
  height: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.darkForeground};
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.surface.normalForeground};
`

const NextDayIndicator = styled.sup`
  position: relative;
  display: inline-block;
  font-size: 0.6em;
  margin-inline-start: ${rem(2)};
`

interface Props {
  departureTime?: string
  arrivalTime?: string
  /** Purchase-time departure clock time, shown struck through beside the live time when the schedule moved. */
  originalDepartureTime?: string
  /** Purchase-time arrival clock time, shown struck through beside the live time when the schedule moved. */
  originalArrivalTime?: string
  arrivalDate?: string
  departureDate?: string
  /** Purchase-time departure calendar date, shown struck through beside the live date when the schedule moved. */
  originalDepartureDate?: string
  /** Purchase-time arrival calendar date, shown struck through beside the live date when the schedule moved. */
  originalArrivalDate?: string
  /**
   * Moment-parseable calendar date (e.g. `YYYY-MM-DD`) used for `+N` day math when
   * `departureDate` / `arrivalDate` are locale-formatted display strings.
   */
  departureDayBasisForDiff?: string
  arrivalDayBasisForDiff?: string
  departureAirport: string
  arrivalAiport: string
  duration?: string
  stops: number
  stopAirports?: Array<string>
  showDate?: boolean
  /** Stack previous and updated values on schedule change snapshot cards. */
  stackChanges?: boolean
  strikeThrough?: boolean
}

function FareFamilyFlightTileV2TimeLine(props: Props) {
  const {
    departureAirport,
    departureTime,
    originalDepartureTime,
    arrivalAiport,
    arrivalTime,
    originalArrivalTime,
    duration,
    stops,
    stopAirports,
    arrivalDate,
    departureDate,
    originalDepartureDate,
    originalArrivalDate,
    departureDayBasisForDiff,
    arrivalDayBasisForDiff,
    showDate = true,
    stackChanges = false,
    strikeThrough = false,
  } = props
  const intl = useIntl()
  const Value = strikeThrough ? 's' : 'span'

  const stopsArray = useMemo(() => {
    return fillArray(stops)
  }, [stops])

  // Calculate number of days ahead for arrival
  const daysAhead = useMemo(() => {
    const departInput = departureDayBasisForDiff ?? departureDate
    const arriveInput = arrivalDayBasisForDiff ?? arrivalDate
    if (!departInput || !arriveInput) {
      return 0
    }
    const departMoment = moment(departInput)
    const arriveMoment = moment(arriveInput)
    if (!departMoment.isValid() || !arriveMoment.isValid()) {
      return 0
    }
    return arriveMoment.diff(departMoment, 'days')
  }, [departureDayBasisForDiff, arrivalDayBasisForDiff, departureDate, arrivalDate])

  const nextDayLabel = intl.formatMessage(
    {
      id: 'Ku95FPFA4Vw52WcByqTw',
      defaultMessage: '+{days}',
      description: 'Indicator showing number of days ahead for overnight flights, e.g. +1',
    },
    { days: daysAhead },
  )

  const showDepartureDate = Boolean(originalDepartureDate) || (showDate && Boolean(departureDate))
  const showArrivalDate = Boolean(originalArrivalDate) || (showDate && Boolean(arrivalDate))

  let stopLabel: string
  if (stopAirports?.length) {
    stopLabel = intl.formatMessage({
      id: 'mhsKKGBw6VOdhf4dulu7',
      defaultMessage: '{count, plural, one {# stop} other {# stops}} · {airports}',
      description: 'Stop count and ordered stopover airport codes on a schedule change flight card timeline',
    }, { count: stops, airports: intl.formatList(stopAirports, { style: 'narrow', type: 'conjunction' }) })
  } else {
    stopLabel = intl.formatMessage({
      id: 'zjq-T_fCULjcaWWSHOLK',
      defaultMessage: '{count, plural, one {# stop} other {# stops}}',
      description: 'Number of stops in flight timeline in the flight search or booking flow',
    }, { count: stops })
  }

  return (
    <TimeLineContainer>
      <Group direction="vertical">
        <Caption variant="large"><Value>{departureAirport}</Value></Caption>
        <Group direction={stackChanges ? 'vertical' : 'horizontal'} gap="0 4" verticalAlign="center">
          {originalDepartureTime && (
            <Heading variant="heading6" colour="light-foreground-alt">
              <s>{originalDepartureTime}</s>
            </Heading>
          )}
          <Heading variant="heading6" colour={originalDepartureTime ? 'highlight-primary-dark' : undefined}>
            <Value>{departureTime}</Value>
          </Heading>
        </Group>
        {showDepartureDate && (
          <Group direction={stackChanges ? 'vertical' : 'horizontal'} gap="0 4" verticalAlign="center">
            {originalDepartureDate && (
              <Caption variant={stackChanges ? 'smallalt' : 'large'} colour="light-foreground-alt">
                <s>{originalDepartureDate}</s>
              </Caption>
            )}
            {departureDate && (
              <Caption variant={stackChanges ? 'smallalt' : 'large'} colour={originalDepartureDate ? 'highlight-primary-dark' : undefined}>
                <Value>{departureDate}</Value>
              </Caption>
            )}
          </Group>
        )}
      </Group>

      <Group direction="vertical" horizontalAlign="center">

        <FlightInfoContainer direction="horizontal" gap="0 2" verticalAlign="center">
          <FlightLineContainer direction="vertical" horizontalAlign="center" gap="8 0">
            <CaptionTextBlock variant="medium"><Value>{duration}</Value></CaptionTextBlock>

            <FlightLine>
              <FlightLineDots direction="horizontal" gap="0 24">
                {stopsArray.map((_, i) => <FlightLineDot key={i} />)}
              </FlightLineDots>
            </FlightLine>

            {stops > 0 && <CaptionTextBlock variant="medium">
              <Value>{stopLabel}
              </Value>
            </CaptionTextBlock>}

            {stops === 0 && <CaptionTextBlock variant="medium" colour="highlight-primary-dark">
              <Value>{intl.formatMessage({
                id: '_1-yGA_edHlstfRILOgn',
                defaultMessage: 'Direct',
                description: 'Label indicating a non-stop direct flight in the flights flow',
              })}
              </Value>
            </CaptionTextBlock>}
          </FlightLineContainer>

          <SolidPlaneDepartureAltIcon size="S" rtlFlip />
        </FlightInfoContainer>
      </Group>

      <Group direction="vertical">
        <Caption variant="large"><Value>{arrivalAiport}</Value></Caption>
        <Group direction={stackChanges ? 'vertical' : 'horizontal'} gap="0 4" verticalAlign="center">
          {originalArrivalTime && (
            <Heading variant="heading6" colour="light-foreground-alt">
              <s>{originalArrivalTime}</s>
            </Heading>
          )}
          <Heading
            variant="heading6"
            colour={originalArrivalTime ? 'highlight-primary-dark' : undefined}
            style={{ position: 'relative' }}
          >
            <Value>{arrivalTime}</Value>
            {daysAhead > 0 && !showArrivalDate && <NextDayIndicator>{nextDayLabel}</NextDayIndicator>}
          </Heading>
        </Group>
        {showArrivalDate && (
          <Group direction={stackChanges ? 'vertical' : 'horizontal'} gap="0 4" verticalAlign="center">
            {originalArrivalDate && (
              <Caption variant={stackChanges ? 'smallalt' : 'large'} colour="light-foreground-alt">
                <s>{originalArrivalDate}</s>
              </Caption>
            )}
            {arrivalDate && (
              <Caption variant={stackChanges ? 'smallalt' : 'large'} colour={originalArrivalDate ? 'highlight-primary-dark' : undefined}>
                <Value>{arrivalDate}</Value>
              </Caption>
            )}
          </Group>
        )}
      </Group>
    </TimeLineContainer>
  )
}

export default FareFamilyFlightTileV2TimeLine
